import React, { useState } from 'react';
import { getModelOptions } from '../config/models';

const CustomerInfo = ({ formData, updateFormData, nextStep, prevStep, currentStep, totalSteps }) => {
  const [validationErrors, setValidationErrors] = useState({});

  const handleInputChange = (field, value) => {
    // If model is changed, also update modelNo in ProductSpecs
    if (field === 'model') {
      updateFormData({ [field]: value, modelNo: value });
    } else {
      updateFormData({ [field]: value });
    }
    // Clear validation error for this field when user starts typing
    if (validationErrors[field]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateRequiredFields = () => {
    const errors = {};
    const requiredFields = {
      customer: 'Customer',
      date: 'Date',
      poNo: 'PO.No',
      poDate: 'P.O. Date',
      invoiceNo: 'Invoice No',
      invoiceDate: 'Invoice Date',
      instrumentSlNo: 'Instrument SI No',
      model: 'Model'
    };

    const dateFields = ['date', 'poDate', 'invoiceDate'];
    const selectFields = ['model'];

    Object.keys(requiredFields).forEach((field) => {
      const value = formData[field];
      if (dateFields.includes(field)) {
        // For date fields, check if empty string
        if (!value || value === '') {
          errors[field] = `${requiredFields[field]} is required`;
        }
      } else if (selectFields.includes(field)) {
        // For select fields, check if empty string
        if (!value || value === '') {
          errors[field] = `${requiredFields[field]} is required`;
        }
      } else {
        // For text fields, check if empty or whitespace only
        if (!value || (typeof value === 'string' && value.trim() === '')) {
          errors[field] = `${requiredFields[field]} is required`;
        }
      }
    });

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNextStep = () => {
    if (validateRequiredFields()) {
      nextStep();
    }
  };

  return (
    <div className="form-section">
      <h3>Customer Information</h3>
      
      <div className="form-row">
        <div className="form-group">
          <label>Customer: <span className="required">*</span></label>
          <div className="input-wrapper">
            <input
              type="text"
              value={formData.customer || ''}
              onChange={(e) => handleInputChange('customer', e.target.value)}
              placeholder="Enter customer name"
              className={validationErrors.customer ? 'error' : ''}
            />
            {validationErrors.customer && (
              <span className="error-message">{validationErrors.customer}</span>
            )}
          </div>
        </div>
        <div className="form-group">
          <label>Date: <span className="required">*</span></label>
          <div className="input-wrapper">
            <input
              type="date"
              value={formData.date || ''}
              onChange={(e) => handleInputChange('date', e.target.value)}
              className={validationErrors.date ? 'error' : ''}
            />
            {validationErrors.date && (
              <span className="error-message">{validationErrors.date}</span>
            )}
          </div>
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>P.O. No: <span className="required">*</span></label>
          <div className="input-wrapper">
            <input
              type="text"
              value={formData.poNo || ''}
              onChange={(e) => handleInputChange('poNo', e.target.value)}
              placeholder="Enter P.O. number"
              className={validationErrors.poNo ? 'error' : ''}
            />
            {validationErrors.poNo && (
              <span className="error-message">{validationErrors.poNo}</span>
            )}
          </div>
        </div>
        <div className="form-group">
          <label>P.O. Date: <span className="required">*</span></label>
          <div className="input-wrapper">
            <input
              type="date"
              value={formData.poDate || ''}
              onChange={(e) => handleInputChange('poDate', e.target.value)}
              className={validationErrors.poDate ? 'error' : ''}
            />
            {validationErrors.poDate && (
              <span className="error-message">{validationErrors.poDate}</span>
            )}
          </div>
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Invoice No: <span className="required">*</span></label>
          <div className="input-wrapper">
            <input
              type="text"
              value={formData.invoiceNo || ''}
              onChange={(e) => handleInputChange('invoiceNo', e.target.value)}
              placeholder="Enter invoice number"
              className={validationErrors.invoiceNo ? 'error' : ''}
            />
            {validationErrors.invoiceNo && (
              <span className="error-message">{validationErrors.invoiceNo}</span>
            )}
          </div>
        </div>
        <div className="form-group">
          <label>Invoice Date: <span className="required">*</span></label>
          <div className="input-wrapper">
            <input
              type="date"
              value={formData.invoiceDate || ''}
              onChange={(e) => handleInputChange('invoiceDate', e.target.value)}
              className={validationErrors.invoiceDate ? 'error' : ''}
            />
            {validationErrors.invoiceDate && (
              <span className="error-message">{validationErrors.invoiceDate}</span>
            )}
          </div>
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Instrument Sl No: <span className="required">*</span></label>
          <div className="input-wrapper">
            <input
              type="text"
              value={formData.instrumentSlNo || ''}
              onChange={(e) => handleInputChange('instrumentSlNo', e.target.value)}
              placeholder="Enter instrument serial number"
              className={validationErrors.instrumentSlNo ? 'error' : ''}
            />
            {validationErrors.instrumentSlNo && (
              <span className="error-message">{validationErrors.instrumentSlNo}</span>
            )}
          </div>
        </div>
        <div className="form-group">
          <label>Model: <span className="required">*</span></label>
          <div className="input-wrapper">
            <select
              value={formData.model || ''}
              onChange={(e) => handleInputChange('model', e.target.value)}
              className={validationErrors.model ? 'error' : ''}
            >
              <option value="">Select a model</option>
              {getModelOptions().map((model) => (
                <option key={model.value} value={model.value}>
                  {model.label}
                </option>
              ))}
            </select>
            {validationErrors.model && (
              <span className="error-message">{validationErrors.model}</span>
            )}
          </div>
        </div>
      </div>

      <div className="navigation-buttons">
        <button 
          className="btn btn-secondary" 
          onClick={prevStep}
          disabled={currentStep === 1}
        >
          Previous
        </button>
        <button 
          className="btn btn-primary" 
          onClick={handleNextStep}
          disabled={currentStep === totalSteps}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default CustomerInfo;
